import React, { useEffect, useRef, useState, type ReactElement } from "react";
import { Box, Typography } from "@mui/material";
import axios from "axios";
import { streamsApi } from "../../api/flashbackApi/streams.ts";
import { HttpError } from "../../api/flashbackApi/http.ts";
import {
  getFlashbackDummyVideoUrl,
  isFlashbackDummyStreamEnabled,
} from "../../api/flashbackApi/flashbackDummyStream.ts";
import {
  startLiveHlsPlayback,
  type LiveHlsPlayback,
} from "../../lib/live-stream.ts";
import type { CameraStatus } from "../../api/flashbackApi/types.ts";

type Phase = "idle" | "loading" | "playing" | "offline" | "error";

interface Props {
  cameraId: string;
  status: CameraStatus;
}

/**
 * Margin around the viewport in which a camera tile is considered "visible"
 * enough to warm its HLS player. Pre-warming nearby tiles keeps scrolling
 * smooth while still capping concurrent hls.js instances when the grid is
 * tall.
 */
const VISIBILITY_ROOT_MARGIN_PX = 200;

/**
 * Once an offscreen tile stays hidden for this long we release its hls.js
 * instance. Re-mounting on the next intersection is cheap; keeping dozens of
 * background workers running is what crashes the page.
 */
const PAUSE_AFTER_HIDDEN_MS = 8_000;

export function LivePreview({ cameraId, status }: Props): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return undefined;
    }

    let hideTimer: number | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (hideTimer !== null) {
              window.clearTimeout(hideTimer);
              hideTimer = null;
            }
            setIsVisible(true);
          } else {
            if (hideTimer !== null) window.clearTimeout(hideTimer);
            hideTimer = window.setTimeout(() => {
              setIsVisible(false);
              hideTimer = null;
            }, PAUSE_AFTER_HIDDEN_MS);
          }
        }
      },
      { rootMargin: `${VISIBILITY_ROOT_MARGIN_PX}px`, threshold: 0.05 },
    );
    observer.observe(node);

    return () => {
      observer.disconnect();
      if (hideTimer !== null) window.clearTimeout(hideTimer);
    };
  }, []);

  useEffect(() => {
    if (isFlashbackDummyStreamEnabled()) {
      const v = videoRef.current;
      if (!v || !isVisible) return undefined;
      setPhase("loading");
      setError(null);
      v.loop = true;
      v.muted = true;
      v.playsInline = true;
      v.src = getFlashbackDummyVideoUrl();
      void v
        .play()
        .then(() => setPhase("playing"))
        .catch(() => {
          setError("Could not start dummy preview video");
          setPhase("error");
        });
      return () => {
        v.pause();
        v.removeAttribute("src");
        v.load();
      };
    }

    if (status !== "active") {
      setPhase("offline");
      return;
    }

    if (!isVisible) {
      setPhase("idle");
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    let hlsPlayback: LiveHlsPlayback | null = null;

    setPhase("loading");
    setError(null);

    streamsApi
      .getDescriptor(cameraId, controller.signal)
      .then(async (descriptor) => {
        if (cancelled || !videoRef.current) return;
        if (descriptor.mediamtxStatus !== "registered") {
          throw new Error(
            descriptor.mediamtxError ?? "Stream not registered with MediaMTX",
          );
        }
        if (!descriptor.hlsUrl?.trim()) {
          throw new Error("HLS stream URL is not available for this camera");
        }
        hlsPlayback = await startLiveHlsPlayback(
          descriptor.hlsUrl,
          videoRef.current,
          (message) => {
            if (cancelled) return;
            setError(message);
            setPhase("error");
          },
        );
        if (cancelled) {
          hlsPlayback.destroy();
          return;
        }
        void videoRef.current.play().catch(() => undefined);
        setPhase("playing");
      })
      .catch((err: unknown) => {
        if (cancelled || axios.isCancel(err) || controller.signal.aborted)
          return;
        const message =
          err instanceof HttpError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Failed to start stream";
        setError(message);
        setPhase("error");
      });

    return () => {
      cancelled = true;
      controller.abort();
      hlsPlayback?.destroy();
    };
  }, [cameraId, status, retryToken, isVisible]);

  return (
    <Box ref={containerRef} sx={{ position: "absolute", inset: 0 }}>
      <Box
        component="video"
        ref={videoRef}
        autoPlay
        muted
        playsInline
        preload="metadata"
        sx={{
          height: "100%",
          width: "100%",
          backgroundColor: "#000",
          objectFit: "cover",
          objectPosition: "center",
          transition: "opacity 0.2s",
          opacity: phase === "playing" ? 1 : 0,
        }}
      />

      {phase !== "playing" && (
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            backgroundColor: "rgba(245, 247, 250, 0.45)",
          }}
        >
          <Box
            sx={{
              pointerEvents: "auto",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
              px: 2,
            }}
          >
            {phase === "loading" && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: 12,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--app-text-secondary)",
                }}
              >
                Connecting…
              </Typography>
            )}
            {phase === "offline" && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: 12,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "var(--app-text-secondary)",
                }}
              >
                Camera offline
              </Typography>
            )}
            {phase === "error" && (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--app-error)",
                  }}
                >
                  Stream unavailable
                </Typography>
                {error && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 12,
                      color: "var(--app-text-secondary)",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {error}
                  </Typography>
                )}
                <Box
                  component="button"
                  type="button"
                  onClick={() => setRetryToken((n) => n + 1)}
                  sx={{
                    borderRadius: "4px",
                    border: "1px solid var(--app-border)",
                    backgroundColor: "var(--app-surface)",
                    px: 1,
                    py: 0.25,
                    fontSize: 11,
                    color: "var(--app-text-primary)",
                    cursor: "pointer",
                    "&:hover": { borderColor: "var(--app-primary)" },
                    "&:focus-visible": {
                      outline: "none",
                      boxShadow: "0 0 0 2px rgba(41, 50, 229, 0.35)",
                    },
                  }}
                >
                  Retry
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
